import { Injectable, inject } from '@angular/core';

import { Sitecore, TopNavigationConfig } from '@frontend/sports/common/client-config-data-access';
import { TopMenuContentItem } from '@frontend/sports/navigation/core/feature/models';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { DslService } from '@frontend/vanilla/core';
import { linkedTransferState, tapIfServer } from '@frontend/vanilla/ssr';
import { Observable, combineLatest, defer, map, of, startWith, switchMap } from 'rxjs';

import { NavigationItemsMapper } from '../navigation-items-mapper';
import { BaseNavigationItemsProvider } from './base-navigation-items.provider';

@Injectable({ providedIn: 'root' })
export class FixedNavigationItemsProvider extends BaseNavigationItemsProvider {
    private navigationConfig = inject(TopNavigationConfig);
    private dslService = inject(DslService);
    protected mapper = inject(NavigationItemsMapper);
    private sitecore = inject(Sitecore);
    private urlHelper = inject(UrlHelperService);

    readonly navigationItems$: Observable<TopMenuContentItem[]>;

    readonly navigationItemsTransferState = linkedTransferState<TopMenuContentItem[]>('fixed-navigation-items', []);

    constructor() {
        super();

        const evaluatedItems$ = defer(() =>
            of(this.navigationConfig.topNavigationItems?.length ? this.navigationConfig.fixedItems : this.navigationConfig.afterSports),
        ).pipe(switchMap((data) => this.dslService.evaluateContent(data)));

        this.navigationItems$ = combineLatest([evaluatedItems$, this.hasLiveItems$]).pipe(
            map(([items, hasLive]) => {
                const options = {
                    hasLive,
                    isFixedItem: true,
                };

                return this.mapper.mapItems(items, '', options);
            }),
            tapIfServer((items) => this.navigationItemsTransferState.set(items)),
            startWith(this.navigationItemsTransferState()),
        );
    }

    public getDefaultItems(): TopMenuContentItem[] {
        return [
            {
                name: 'betfinder',
                text: this.sitecore.globalMessages.Search,
                url: '',
                class: 'theme-search before-separator',
                trackEvent: this.mapper.getTrackingData('Search'),
                parameters: {},
                resources: {},
                popup: 'betfinder',
            } as TopMenuContentItem,
            {
                name: 'favourites',
                text: this.sitecore.globalMessages.Favourites,
                url: this.urlHelper.getFavouriteUrl(),
                class: 'sports-favourites',
                trackEvent: this.mapper.getTrackingData('Favourites'),
                parameters: {},
                resources: {},
            } as TopMenuContentItem,
            {
                name: 'mybets',
                text: this.sitecore.globalMessages.MyBetsTitle,
                url: this.urlHelper.getMyBetsUrl(),
                class: 'theme-mybets',
                trackEvent: this.mapper.getTrackingData('MyBets'),
                parameters: {},
                resources: {},
            } as TopMenuContentItem,
        ];
    }
}
